import { useState } from 'react';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import {
  useRemoveAccountProviderKey,
  useSetAccountDefaultProvider,
  useSetAccountProviderEnabled,
  useVerifyAccountProviderKey,
} from '../../hooks/useAccountModels';
import type { AccountModelProvider } from '../../types/accountModels';
import { refusalReason } from '../../utils/apiRefusal';
import { ProviderRow } from '../models/ProviderRow';
import { PolicySwitch } from './PolicySwitch';
import { ProviderKeyDialog } from './ProviderKeyDialog';

/**
 * Settings › Account › Models — Providers (task 1147, steps 2–5).
 *
 * One row per Model Provider the platform has enabled, saying whose key the
 * account's calls use, with the key actions, the provider's on/off for this
 * account, and the default-provider control below the rows.
 *
 * The row and its key chip are the shared `ProviderRow` in `account` scope
 * (task 1167), the same component the platform Models page uses in `platform`
 * scope.
 */

interface ProvidersSectionProps {
  providers: AccountModelProvider[];
  canManage: boolean;
}

export function ProvidersSection({ providers, canManage }: ProvidersSectionProps) {
  const toast = useToast();
  const verify = useVerifyAccountProviderKey();
  const remove = useRemoveAccountProviderKey();
  const setEnabled = useSetAccountProviderEnabled();
  const setDefault = useSetAccountDefaultProvider();

  const [keyDialog, setKeyDialog] = useState<{ provider: AccountModelProvider; replace: boolean } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<AccountModelProvider | null>(null);
  /** The provider whose key was just saved: its row carries the what-changed note. */
  const [savedProviderId, setSavedProviderId] = useState<string | null>(null);

  const enabledProviders = providers.filter((p) => p.enabled);
  const defaultProviderId = providers.find((p) => p.isAccountDefault)?.id ?? null;

  const handleVerify = async (provider: AccountModelProvider) => {
    try {
      const result = await verify.mutateAsync(provider.id);
      if (result.ok) {
        toast.success(
          'Key verified',
          `${result.reachableCount} of ${result.totalCount} models reachable on your ${provider.displayName} key.`,
        );
      } else {
        toast.error(`${provider.displayName} rejected the key`, result.failureMessage);
      }
    } catch (error) {
      toast.error('Could not verify the key', refusalReason(error));
    }
  };

  const confirmRemove = async () => {
    if (!pendingRemove) return;
    const provider = pendingRemove;
    try {
      await remove.mutateAsync(provider.id);
      if (savedProviderId === provider.id) setSavedProviderId(null);
      toast.success('Key removed', `Your AI Services use Innovate's key for ${provider.displayName} again.`);
    } catch (error) {
      toast.error('Could not remove the key', refusalReason(error));
    } finally {
      setPendingRemove(null);
    }
  };

  const handleToggle = async (provider: AccountModelProvider) => {
    try {
      await setEnabled.mutateAsync({ providerId: provider.id, enabled: !provider.enabled });
    } catch (error) {
      toast.error(`Could not turn ${provider.displayName} ${provider.enabled ? 'off' : 'on'}`, refusalReason(error));
    }
  };

  const handleDefaultProvider = async (providerId: string) => {
    if (providerId === defaultProviderId) return;
    try {
      await setDefault.mutateAsync(providerId);
    } catch (error) {
      toast.error('Could not change the default provider', refusalReason(error));
    }
  };

  return (
    <section
      data-testid="account-models-providers"
      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">Providers &amp; keys</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Which Model Providers this account&apos;s AI Services may call, and whose key they call with. Applies to every
        service built in this account.
      </p>

      {providers.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-testid="account-models-no-providers">
          No Model Providers are enabled on the platform yet.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-gray-200 dark:divide-gray-700">
          {providers.map((provider) => (
            <li key={provider.id} className="py-4" data-testid={`provider-row-${provider.key}`}>
              <ProviderRow
                scope="account"
                provider={provider}
                canManage={canManage}
                showSavedNote={savedProviderId === provider.id}
                verifying={verify.isPending && verify.variables === provider.id}
                onUseOwnKey={() => setKeyDialog({ provider, replace: false })}
                onReplace={() => setKeyDialog({ provider, replace: true })}
                onVerify={() => handleVerify(provider)}
                onRemove={() => setPendingRemove(provider)}
                trailing={
                  <PolicySwitch
                    checked={provider.enabled}
                    onToggle={() => handleToggle(provider)}
                    disabled={!canManage || setEnabled.isPending}
                    label={`Allow ${provider.displayName}`}
                    testId={`provider-toggle-${provider.key}`}
                  />
                }
              />
            </li>
          ))}
        </ul>
      )}

      {enabledProviders.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-4 border-t border-gray-200 pt-4 dark:border-gray-700">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" id="default-provider-label">
              Default provider
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              New agents start on this provider&apos;s default Model.
            </p>
          </div>
          <div
            role="radiogroup"
            aria-labelledby="default-provider-label"
            className="inline-flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-600"
          >
            {enabledProviders.map((provider) => {
              const selected = provider.id === defaultProviderId;
              return (
                <button
                  key={provider.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={!canManage || setDefault.isPending}
                  onClick={() => handleDefaultProvider(provider.id)}
                  data-testid={`default-provider-${provider.key}`}
                  className={`rounded-md px-3 py-1 text-sm font-medium disabled:cursor-not-allowed ${
                    selected
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {provider.displayName}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <ProviderKeyDialog
        provider={keyDialog?.provider ?? null}
        replace={keyDialog?.replace ?? false}
        onClose={() => setKeyDialog(null)}
        onSaved={(provider) => {
          setKeyDialog(null);
          setSavedProviderId(provider.id);
          toast.success('Key saved and verified');
        }}
      />

      <ConfirmDialog
        isOpen={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        onConfirm={confirmRemove}
        title={`Remove your ${pendingRemove?.displayName ?? ''} key?`}
        message={`All of this account's AI Services will go back to Innovate's key for ${pendingRemove?.displayName ?? 'this provider'}, and those tokens return to your invoice.`}
        confirmLabel="Remove key"
        confirmStyle="danger"
        isLoading={remove.isPending}
      />
    </section>
  );
}
